import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Building2,
  Trees,
  IdCard,
  Landmark,
  Calculator,
  ScrollText,
  Receipt,
  ArrowLeftRight,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    icon: Building2,
    title: "Mainland Setup",
    copy: "DED trade license with full UAE market access. Most flexible structure for retail, services and SMEs.",
    to: "/company-formation/mainland",
  },
  {
    icon: Trees,
    title: "Freezone Setup",
    copy: "0% tax on qualifying income, 100% foreign ownership and the fastest path to a UAE entity.",
    to: "/company-formation/freezone",
  },
  {
    icon: IdCard,
    title: "Visa Services",
    copy: "Investor, Golden, Green and family residency — handled end-to-end with Emirates ID and medical.",
    to: "/visa",
  },
  {
    icon: Landmark,
    title: "Corporate Banking",
    copy: "Direct introductions to senior RMs at Emirates NBD, Wio, Mashreq, FAB and tier-1 international banks.",
    to: "/banking",
  },
  {
    icon: Calculator,
    title: "Accounting",
    copy: "Cloud-native bookkeeping, monthly close and IFRS-aligned financial statements — investor-ready always.",
    to: "/services/accounting",
  },
  {
    icon: ScrollText,
    title: "Corporate Tax",
    copy: "FTA registration, qualifying-income analysis and annual return filing for UAE 9% corporate tax.",
    to: "/services/corporate-tax",
  },
  {
    icon: Receipt,
    title: "VAT",
    copy: "VAT registration, return filing, refund claims and FTA audit support — your TRN, properly managed.",
    to: "/services/vat",
  },
  {
    icon: ArrowLeftRight,
    title: "Transfer Pricing",
    copy: "OECD-aligned Master File, Local File and benchmarking studies for related-party transactions.",
    to: "/services/transfer-pricing",
  },
];

export default function ServicesV2() {
  return (
    <section
      data-testid="v2-services"
      className="relative bg-[#F8FAFC] py-10 md:py-14"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-7">
          <p className="overline text-[#3366FF]">Our services</p>
          <div className="gold-rule mt-5 mb-6" />
          <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
            End-to-end <span className="font-black text-[#002366]">UAE business support</span>.
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            From your first trade license to your annual tax return — a single
            partner, accountable across the full lifecycle.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
          {SERVICES.map(({ icon: Icon, title, copy, to }, i) => (
            <motion.div
              key={title}
              data-testid={`v2-service-${i}`}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
              className="bg-white p-7 group hover:bg-[#EEF2FF]/60 transition-colors duration-500 relative"
            >
              <div className="w-11 h-11 rounded-sm bg-white border border-[#3366FF]/30 text-[#3366FF] inline-flex items-center justify-center group-hover:bg-[#002366] group-hover:text-[#6B8AFF] group-hover:border-[#002366] transition-colors">
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <h3 className="mt-4 font-heading font-semibold text-base md:text-lg tracking-tight text-[#0F172A]">
                {title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed min-h-[80px]">
                {copy}
              </p>
              <Link
                to={to}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-[#002366] group-hover:text-[#3366FF] transition-colors"
              >
                Learn more
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <span className="absolute top-5 right-5 font-heading font-black text-xs text-slate-300 group-hover:text-[#3366FF]/60 transition-colors">
                0{i + 1}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
